/**
 * App.jsx — Updated
 *
 * Added new invoice module routes alongside existing accounting routes.
 * ALL existing routes are preserved exactly.
 *
 * PASTE AT: frontend/src/App.jsx  (REPLACE entire file)
 */

import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';
import { AppLayout } from './components/layout/AppLayout';

// ── Existing imports (unchanged) ──────────────────────────────
import { InvoicesPage }         from './pages/admin/accounting/Invoices';
import { CreditNotesPage }      from './pages/admin/accounting/CreditNotes';
import { EInvoicesPage }        from './pages/admin/accounting/EInvoices';
import { PurchaseOrdersPage }   from './pages/admin/accounting/PurchaseOrders';
import { SalesOrdersPage }      from './pages/admin/accounting/SalesOrders';
import { Login }                from './pages/auth/Login';
import { AdminDashboard }       from './pages/admin/Dashboard';
import { UsersPage }            from './pages/admin/Users';
import { ProjectsPage }         from './pages/admin/Projects';
import { ProjectDetail }        from './pages/admin/projects/ProjectDetail';
import { TasksPage }            from './pages/admin/Tasks';
import { CalendarPage }         from './pages/admin/Calendar';
import { InventoryPage }        from './pages/admin/Inventory';
import { DataEntryPage }        from './pages/admin/DataEntry';
import { SettingsPage }         from './pages/admin/Settings';
import { CustomersPage }        from './pages/admin/contacts/Customers';
import { VendorsPage }          from './pages/admin/contacts/Vendors';
import { ContractorsPage }      from './pages/admin/contacts/Contractors';
import { AllPage }              from './pages/admin/accounting/All';
import { SalesPage }            from './pages/admin/accounting/Sales';
import { PurchasesPage }        from './pages/admin/accounting/Purchases';
import { QuotationsPage }       from './pages/admin/accounting/Quotations';
import { ExpensesPage }         from './pages/admin/accounting/Expenses';
import { IncomePage }           from './pages/admin/accounting/Income';
import { ProformaInvoicesPage } from './pages/admin/accounting/ProformaInvoices';
import { DeliveryChallansPage } from './pages/admin/accounting/DeliveryChallans';
import { DebitNotesPage }       from './pages/admin/accounting/DebitNotes';
import { AnalyticsDashboard }   from './pages/admin/reports/AnalyticsDashboard';
import { ProfitLossReport }     from './pages/admin/reports/ProfitLossReport';
import { GSTReport }            from './pages/admin/reports/GSTReport';
import { LedgerReport }         from './pages/admin/reports/LedgerReport';
import { EmployeeDashboard }    from './pages/employee/Dashboard';
import { AccountantDashboard }  from './pages/accountant/Dashboard';
import { ReportsPage }          from './pages/accountant/Reports';
import { ContractorPortal }     from './pages/admin/contractor/ContractorPortal';
import { DocumentsPage }        from './pages/admin/Documents';
import { GSTPage }              from './pages/admin/accounting/GSTPage';
import { PartyLedgerPage }      from './pages/admin/accounting/PartyLedger';

// ── NEW: Invoice module pages ─────────────────────────────────
import { InvoiceListPage } from './pages/admin/accounting/InvoiceList';

// ── NEW: Working report pages ─────────────────────────────────
import { SalesReport }       from './pages/admin/reports/SalesReport';
import { GSTReportNew }      from './pages/admin/reports/GSTReportNew';
import { OutstandingReport } from './pages/admin/reports/OutstandingReport';
import { CollectionReport }  from './pages/admin/reports/CollectionReport';

/* ── Route guards ────────────────────────────────────────────── */
function PrivateRoute({ children, roles }) {
  const { user, loading } = useAuth();
  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="flex flex-col items-center gap-3">
        <div className="w-10 h-10 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin" />
        <p className="text-sm text-gray-400">Loading...</p>
      </div>
    </div>
  );
  if (!user) return <Navigate to="/login" replace />;
  const roleHome = user.role === 'contractor' ? '/contractor' : '/' + user.role;
  if (roles && !roles.includes(user.role)) return <Navigate to={roleHome} replace />;
  return children;
}

function PublicRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) {
    const dest = user.role === 'contractor' ? '/contractor' : '/' + user.role;
    return <Navigate to={dest} replace />;
  }
  return children;
}

// Invoice module route paths — added inline in each role block below

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />

          {/* ── Admin ─────────────────────────────────────────── */}
          <Route path="/admin" element={<PrivateRoute roles={['admin']}><AppLayout /></PrivateRoute>}>
            <Route index                        element={<AdminDashboard />} />
            <Route path="users"                 element={<UsersPage />} />
            <Route path="projects"              element={<ProjectsPage />} />
            <Route path="projects/:id"          element={<ProjectDetail />} />
            <Route path="tasks"                 element={<TasksPage />} />
            <Route path="calendar"              element={<CalendarPage />} />
            <Route path="inventory"             element={<InventoryPage />} />
            <Route path="dataentry"             element={<DataEntryPage />} />
            <Route path="settings"              element={<SettingsPage />} />
            <Route path="documents"             element={<DocumentsPage />} />

            {/* ── NEW invoice module routes ── */}
            <Route path="invoices"          element={<InvoiceListPage docType="INVOICE" />} />
            <Route path="quotations"        element={<InvoiceListPage docType="QUOTE" />} />
            <Route path="proforma"          element={<InvoiceListPage docType="PROFORMA" />} />
            <Route path="sales-orders"      element={<InvoiceListPage docType="SALES_ORDER" />} />
            <Route path="delivery-challans" element={<InvoiceListPage docType="DELIVERY_CHALLAN" />} />
            <Route path="credit-notes"      element={<InvoiceListPage docType="CREDIT_NOTE" />} />
            <Route path="debit-notes"       element={<InvoiceListPage docType="DEBIT_NOTE" />} />

            {/* ── Existing accounting routes (unchanged) ── */}
            <Route path="accounting"                element={<AllPage />} />
            <Route path="accounting/sales"          element={<SalesPage />} />
            <Route path="accounting/purchases"      element={<PurchasesPage />} />
            <Route path="accounting/quotations"     element={<QuotationsPage />} />
            <Route path="accounting/proforma"       element={<ProformaInvoicesPage />} />
            <Route path="accounting/challans"       element={<DeliveryChallansPage />} />
            <Route path="accounting/debitnotes"     element={<DebitNotesPage />} />
            <Route path="accounting/expenses"       element={<ExpensesPage />} />
            <Route path="accounting/income"         element={<IncomePage />} />
            <Route path="accounting/invoices"       element={<InvoicesPage />} />
            <Route path="accounting/creditnotes"    element={<CreditNotesPage />} />
            <Route path="accounting/einvoices"      element={<EInvoicesPage />} />
            <Route path="accounting/purchaseorders" element={<PurchaseOrdersPage />} />
            <Route path="accounting/salesorders"    element={<SalesOrdersPage />} />
            <Route path="accounting/gst"            element={<GSTPage />} />
            <Route path="accounting/ledger"         element={<PartyLedgerPage />} />

            {/* ── Contacts ── */}
            <Route path="contacts/customers"    element={<CustomersPage />} />
            <Route path="contacts/vendors"      element={<VendorsPage />} />
            <Route path="contacts/contractors"  element={<ContractorsPage />} />

            {/* ── Reports ── */}
            <Route path="analytics"          element={<AnalyticsDashboard />} />
            <Route path="reports/sales"      element={<SalesReport />} />
            <Route path="reports/gst"        element={<GSTReportNew />} />
            <Route path="reports/outstanding"element={<OutstandingReport />} />
            <Route path="reports/collection" element={<CollectionReport />} />
            <Route path="reports/pl"         element={<ProfitLossReport />} />
            <Route path="reports/ledger"     element={<LedgerReport />} />
          </Route>

          {/* ── Employee ──────────────────────────────────────── */}
          <Route path="/employee" element={<PrivateRoute roles={['employee']}><AppLayout /></PrivateRoute>}>
            <Route index               element={<EmployeeDashboard />} />
            <Route path="tasks"        element={<TasksPage myTasksOnly={true} />} />
            <Route path="projects"     element={<ProjectsPage />} />
            <Route path="projects/:id" element={<ProjectDetail />} />
            <Route path="calendar"     element={<CalendarPage />} />
            <Route path="dataentry"    element={<DataEntryPage />} />
            <Route path="settings"     element={<SettingsPage />} />

            {/* Invoice module routes */}
            <Route path="invoices"          element={<InvoiceListPage docType="INVOICE" />} />
            <Route path="quotations"        element={<InvoiceListPage docType="QUOTE" />} />
            <Route path="proforma"          element={<InvoiceListPage docType="PROFORMA" />} />
            <Route path="sales-orders"      element={<InvoiceListPage docType="SALES_ORDER" />} />
            <Route path="delivery-challans" element={<InvoiceListPage docType="DELIVERY_CHALLAN" />} />
            <Route path="credit-notes"      element={<InvoiceListPage docType="CREDIT_NOTE" />} />
            <Route path="debit-notes"       element={<InvoiceListPage docType="DEBIT_NOTE" />} />

            {/* Accounting module routes — purchases, expenses, income etc. */}
            <Route path="accounting"                element={<AllPage />} />
            <Route path="accounting/purchases"      element={<PurchasesPage />} />
            <Route path="accounting/purchaseorders" element={<PurchaseOrdersPage />} />
            <Route path="accounting/debitnotes"     element={<DebitNotesPage />} />
            <Route path="accounting/expenses"       element={<ExpensesPage />} />
            <Route path="accounting/income"         element={<IncomePage />} />
            <Route path="accounting/quotations"     element={<QuotationsPage />} />
            <Route path="accounting/proforma"       element={<ProformaInvoicesPage />} />
            <Route path="accounting/challans"       element={<DeliveryChallansPage />} />
            <Route path="accounting/sales"          element={<SalesPage />} />
          </Route>

          {/* ── Accountant ────────────────────────────────────── */}
          <Route path="/accountant" element={<PrivateRoute roles={['accountant']}><AppLayout /></PrivateRoute>}>
            <Route index           element={<AccountantDashboard />} />
            <Route path="reports"  element={<ReportsPage />} />
            <Route path="settings" element={<SettingsPage />} />

            {/* Accountant invoice routes */}
            <Route path="invoices"          element={<InvoiceListPage docType="INVOICE" />} />
            <Route path="quotations"        element={<InvoiceListPage docType="QUOTE" />} />
            <Route path="proforma"          element={<InvoiceListPage docType="PROFORMA" />} />
            <Route path="sales-orders"      element={<InvoiceListPage docType="SALES_ORDER" />} />
            <Route path="delivery-challans" element={<InvoiceListPage docType="DELIVERY_CHALLAN" />} />
            <Route path="credit-notes"      element={<InvoiceListPage docType="CREDIT_NOTE" />} />
            <Route path="debit-notes"       element={<InvoiceListPage docType="DEBIT_NOTE" />} />

            <Route path="accounting"                element={<AllPage />} />
            <Route path="accounting/sales"          element={<SalesPage />} />
            <Route path="accounting/purchases"      element={<PurchasesPage />} />
            <Route path="accounting/quotations"     element={<QuotationsPage />} />
            <Route path="accounting/proforma"       element={<ProformaInvoicesPage />} />
            <Route path="accounting/challans"       element={<DeliveryChallansPage />} />
            <Route path="accounting/debitnotes"     element={<DebitNotesPage />} />
            <Route path="accounting/expenses"       element={<ExpensesPage />} />
            <Route path="accounting/income"         element={<IncomePage />} />
            <Route path="contacts/customers"        element={<CustomersPage />} />
            <Route path="contacts/vendors"          element={<VendorsPage />} />
            <Route path="contacts/contractors"      element={<ContractorsPage />} />
            <Route path="analytics"          element={<AnalyticsDashboard />} />
            <Route path="reports/pl"         element={<ProfitLossReport />} />
            <Route path="reports/gst"        element={<GSTReportNew />} />
            <Route path="reports/sales"      element={<SalesReport />} />
            <Route path="reports/outstanding"element={<OutstandingReport />} />
            <Route path="reports/collection" element={<CollectionReport />} />
            <Route path="reports/ledger"     element={<LedgerReport />} />
          </Route>

          {/* ── Contractor ────────────────────────────────────── */}
          <Route path="/contractor" element={<PrivateRoute roles={['contractor']}><AppLayout /></PrivateRoute>}>
            <Route index           element={<ContractorPortal />} />
            <Route path="settings" element={<SettingsPage />} />
          </Route>

          <Route path="/"  element={<Navigate to="/login" replace />} />
          <Route path="*"  element={<Navigate to="/login" replace />} />
        </Routes>

        <Toaster position="top-right" toastOptions={{
          duration: 3500,
          style: { borderRadius: '12px', background: '#1f2937', color: '#f9fafb', fontSize: '14px' },
          success: { iconTheme: { primary: '#22c55e', secondary: '#fff' } },
          error:   { iconTheme: { primary: '#ef4444', secondary: '#fff' } },
        }} />
      </AuthProvider>
    </BrowserRouter>
  );
}